import React from "react";
import PropTypes from "prop-types";
import invariant from "invariant";
import Context from "./context";
import ExportRequest from "./model";
import { APIEndpoint } from "api";
import { useDispatch, useStore, batch } from "react-redux";

// interval in ms between refresh
const REFRESH_INTERVAL = 5000;

class WatchedInstance {
	constructor(id, options) {
		this.id = id;
		this.options = options;
	}
}

const stateMachine = (pending, { type, ...action }) => {
	switch(type) {
	case 'watch': {
		// Watch: add an instance to the watch list
		const { apiEndpoint, watch } = action;

		// copy the pending array
		pending = [...pending];

		// find the index of the cell with this APIEndpoint
		const index = pending.findIndex(([apiEndpoint_]) => apiEndpoint_ === apiEndpoint);

		if (index === -1) {
			// Add the APIEndpoint and the instance in the list
			pending.push([apiEndpoint, [watch]]);
		} else {
			// Add the instance in the array
			pending[index][1] = [...pending[index][1], watch];
		}

		return pending;
	}
	case 'complete': {
		// complete: remove an instance from the watch list
		const { apiEndpoint, watch } = action;

		// copy the pending array
		pending = [...pending];

		// find the index of the cell with this APIEndpoint
		const index = pending.findIndex(([apiEndpoint_]) => apiEndpoint_ === apiEndpoint);
		invariant(index >= 0, 'apiEndpoint not found');

		// The list of watched instance for this APIEndpoint
		const watches = [...pending[index][1]];

		// Remove the ID from the list
		watches.splice(watches.indexOf(watch), 1);

		if (watches.length === 0) {
			// no watches to watch anymore
			pending.splice(index, 1);
		} else {
			// replace the list with the id list without the complete one
			pending[index] = [apiEndpoint, watches];
		}

		return pending;
	}
	/* istanbul ignore next */
	default:
		return pending;
	}
};

function ExportWatch({
	apiEndpoint,
	watches,
	onComplete,
}) {
	// Connect to redux store
	const dispatch = useDispatch();
	const store = useStore();

	// Run every interval and check the state of the instances
	// keep a reference to the refresh callback
	const _refs = React.useRef({});
	_refs.current.checkCompleted = () => {
		// Get all the instances from the state
		const completedInstances = apiEndpoint
			.getManyFromState(store.getState(), watches.map(({ id }) => id))
			.filter(i => i.isDone());

		completedInstances
			.forEach(instance => onComplete(apiEndpoint, instance));
	};

	_refs.current.refresh = () => {
		// Get all watches for which the associated ExportRequest instance is
		// missing or undone
		const refreshableWatches = apiEndpoint
		// For each watch, return the matching instance in the state
			.getManyFromState(store.getState(), watches.map(({ id }) => id), { ignoreMissing: false })
		// if the instance is missing or not done, return its index
			.map((instance, i) => instance && instance.isDone() ? -1 : i)
		// Prune -1 index
			.filter(index => index >= 0)
		// map index to WatchedInstance
			.map(index => watches[index]);

		batch(() => {
			// refresh uncompleted instances
			refreshableWatches
				.forEach(instance => {
					dispatch(apiEndpoint.retrieve(instance.id, instance.options));
				});
		});
	};

	// At each interval retrieve the export that are not yet finished
	React.useEffect(() => {
		const timeoutID = setInterval(() => _refs.current.refresh(), REFRESH_INTERVAL);
		return () => clearInterval(timeoutID);
	}, []);

	// Check completion after each change of the store
	React.useEffect(() => {
		return store.subscribe(() => _refs.current.checkCompleted());
	// eslint-disable-next-line
	}, []);

	return null;
}

ExportWatch.propTypes = {
	apiEndpoint: PropTypes.instanceOf(APIEndpoint).isRequired,
	onComplete: PropTypes.func.isRequired,
	watches: PropTypes.arrayOf(PropTypes.instanceOf(WatchedInstance)).isRequired,
};

export default function ExportProvider({
	onComplete,
	onWatch,
	children,
}) {
	const [pending, dispatch] = React.useReducer(stateMachine, []);

	const watch = React.useCallback((apiEndpoint, instance, options) => {
		invariant(instance instanceof ExportRequest, 'instance is required');
		invariant(apiEndpoint instanceof APIEndpoint, 'apiEndpoint is required');

		// Register the watched export request
		dispatch({
			type: 'watch',
			apiEndpoint,
			watch: new WatchedInstance(instance.getKey(), options),
		});

		onWatch(instance, apiEndpoint);
	}, [onWatch]);

	const complete = React.useCallback((apiEndpoint, instance) => {
		invariant(instance instanceof ExportRequest, 'instance is not a an ExportRequest');

		dispatch({
			type: 'complete',
			apiEndpoint,
			instance,
		});

		onComplete(instance, apiEndpoint);
	}, [onComplete]);


	return <Context.Provider value={ {
		watch,
	} } >
		{
			// Render a ExportWatch for each APIEndpoint
			pending.map(([apiEndpoint, watches]) => <ExportWatch
				key={ apiEndpoint.index.toString() }
				apiEndpoint={ apiEndpoint }
				watches={ watches }
				onComplete={ complete }
			/>)
		}
		{ children }
	</Context.Provider>;
}

ExportProvider.propTypes = {
	onComplete: PropTypes.func,
	onWatch: PropTypes.func,
	children: PropTypes.node.isRequired,
};
